import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { ArrowRightLeft } from 'lucide-react';
import { SortableHeader, sortData, toggleSort } from './sortUtils';

export default function TransferStockTab({ stockData, selectedItem, fmtNum, loadStockDetail }) {
  const [tfFrom, setTfFrom] = useState('');
  const [tfTo, setTfTo] = useState('');
  const [tfQty, setTfQty] = useState('');
  const [tfNotes, setTfNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [allLocations, setAllLocations] = useState([]);
  const [tfSort, setTfSort] = useState({ key: '', dir: 'asc' });

  useEffect(() => {
    api.get('/api/inventory/locations').then(r => setAllLocations(r.data.data || [])).catch(() => {});
  }, []);

  const itemLocations = stockData?.locations || [];
  const fromOptions = itemLocations.filter(l => l.current_stock > 0);
  const selectedFromLoc = fromOptions.find(l => l.location_code === tfFrom);
  const maxTransferQty = selectedFromLoc ? selectedFromLoc.current_stock : 0;

  const mergedLocations = useMemo(() => {
    const map = new Map();
    allLocations.forEach(l => {
      if (l.active !== false) {
        map.set(l.location_code, { location_code: l.location_code, description: l.description || '', currentStock: null });
      }
    });
    itemLocations.forEach(l => {
      const existing = map.get(l.location_code);
      map.set(l.location_code, {
        location_code: l.location_code,
        description: existing?.description || l.location_description || l.description || '',
        currentStock: l.current_stock,
      });
    });
    return Array.from(map.values());
  }, [allLocations, itemLocations]);

  const toOptions = mergedLocations.filter(l => l.location_code !== tfFrom);

  const submitTransfer = async () => {
    const qty = parseFloat(tfQty);
    if (!qty || !tfFrom || !tfTo) { toast.error('All transfer fields are required'); return; }
    if (tfFrom === tfTo) { toast.error('Source and destination must be different'); return; }
    if (qty > maxTransferQty) { toast.error(`Cannot transfer more than available stock (${fmtNum(maxTransferQty)})`); return; }
    setSubmitting(true);
    try {
      const existsAtTo = itemLocations.some(l => l.location_code === tfTo);
      if (!existsAtTo) {
        await api.post(`/api/inventory/stock/${selectedItem}/add-location`, { location_code: tfTo });
      }
      const r = await api.post(`/api/inventory/stock/${selectedItem}/transfer`, {
        from_location: tfFrom, to_location: tfTo, quantity: qty, notes: tfNotes,
      });
      toast.success(r.data.message);
      setTfQty(''); setTfNotes(''); setTfFrom(''); setTfTo('');
      loadStockDetail(selectedItem);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Transfer failed')); }
    setSubmitting(false);
  };

  return (
    <div className="max-w-xl">
      <h3 className="font-semibold text-sm mb-3">Transfer Stock Between Locations</h3>

      {itemLocations.length > 0 && (
        <div className="border rounded-xl overflow-hidden mb-4" data-testid="transfer-stock-summary">
          <div className="px-4 py-2 bg-muted/50 border-b font-semibold text-xs">Current Stock by Location</div>
          <table className="w-full text-xs">
            <thead><tr className="border-b bg-muted/20">
              <SortableHeader label="Location" sortKey="location_code" currentSort={tfSort} onSort={toggleSort(setTfSort)} />
              <SortableHeader label="Current Stock" sortKey="current_stock" currentSort={tfSort} onSort={toggleSort(setTfSort)} className="text-right" />
              <SortableHeader label="Value" sortKey="inventory_value" currentSort={tfSort} onSort={toggleSort(setTfSort)} className="text-right" />
            </tr></thead>
            <tbody>{sortData(itemLocations, tfSort.key, tfSort.dir).map(l => (
              <tr key={l.location_code} className="border-b">
                <td className="px-4 py-1.5 font-mono font-semibold">{l.location_code}{l.location_description ? ` — ${l.location_description}` : ''}</td>
                <td className="px-4 py-1.5 text-right">{fmtNum(l.current_stock)}</td>
                <td className="px-4 py-1.5 text-right">{fmtNum(l.inventory_value || 0)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {fromOptions.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No locations with stock to transfer from. Adjust stock at a location first.</p>
      ) : (
        <div className="space-y-3 border rounded-xl p-4" data-testid="transfer-stock-form">
          <div className="space-y-1">
            <Label className="text-xs font-semibold">From Location (Source)</Label>
            <select value={tfFrom} onChange={e => setTfFrom(e.target.value)}
              data-testid="transfer-from-select"
              className="h-9 w-full rounded-lg border border-input bg-background px-2 text-sm">
              <option value="">Select source location...</option>
              {fromOptions.map(l => (
                <option key={l.location_code} value={l.location_code}>
                  {l.location_code}{l.location_description ? ` — ${l.location_description}` : ''} (Available: {fmtNum(l.current_stock)})
                </option>
              ))}
            </select>
            {tfFrom && <p className="text-[10px] text-muted-foreground">Available to transfer: <span className="font-bold text-primary">{fmtNum(maxTransferQty)}</span></p>}
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold">To Location (Destination)</Label>
            <select value={tfTo} onChange={e => setTfTo(e.target.value)}
              data-testid="transfer-to-select"
              className="h-9 w-full rounded-lg border border-input bg-background px-2 text-sm">
              <option value="">Select destination location...</option>
              {toOptions.map(l => {
                const stockInfo = l.currentStock != null ? ` (Current stock: ${fmtNum(l.currentStock)})` : ' (New for this item)';
                return (
                  <option key={l.location_code} value={l.location_code}>
                    {l.location_code}{l.description ? ` — ${l.description}` : ''}{stockInfo}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Quantity to Transfer</Label>
            <Input type="number" step="0.01" min="0.01" max={maxTransferQty || undefined}
              value={tfQty} onChange={e => setTfQty(e.target.value)}
              data-testid="transfer-qty-input"
              className="h-9 text-sm" placeholder={maxTransferQty ? `Max: ${fmtNum(maxTransferQty)}` : 'Enter quantity'} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notes / Reason</Label>
            <Textarea value={tfNotes} onChange={e => setTfNotes(e.target.value)} rows={2} className="text-sm"
              data-testid="transfer-notes-input" placeholder="Optional: reason for transfer" />
          </div>
          <Button onClick={submitTransfer} disabled={submitting || !tfFrom || !tfTo || !tfQty}
            data-testid="transfer-submit-btn">
            <ArrowRightLeft className="h-3.5 w-3.5 mr-1" /> {submitting ? 'Transferring...' : 'Transfer Stock'}
          </Button>
        </div>
      )}
    </div>
  );
}
